import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mountain, Heart, ExternalLink, Loader2, Sparkles, Clock,
  Users, AlertTriangle, MapPin, ChevronLeft,
} from "lucide-react";
import Header from "../components/app/Header";
import { api } from "../lib/api";

/**
 * /charities — Public page describing the charities CampCompass supports.
 *
 * Mountain Rescue is the inaugural charity; the layout supports any number
 * of charities (each rendered as its own panel). The hero pulls the live
 * donation total from /api/charity/donations so the page reads as a
 * meaningful "thank you" once the first donation lands.
 */

function formatGBP(n) {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `£${Math.round(n)}`;
  }
}

const STAT_ICONS = {
  "Volunteer team members": Users,
  "Callouts per year": AlertTriangle,
  "Volunteer hours given": Clock,
  "Cost per callout": Sparkles,
  "What they cover": MapPin,
  "Cost to the rescued": Heart,
};

function CharityPanel({ charity, total }) {
  return (
    <section
      className="mb-12 border-2 hairline-strong bg-white overflow-hidden"
      data-testid={`charity-panel-${charity.slug}`}
    >
      {/* Hero strip */}
      <div
        className="text-white p-6 sm:p-8 relative overflow-hidden"
        style={{ background: charity.hero_color || "#0e0e0e" }}
      >
        <div
          className="absolute -right-6 -bottom-6 text-[160px] leading-none opacity-15 select-none pointer-events-none"
          aria-hidden="true"
        >
          {charity.logo_emoji}
        </div>
        <div className="relative">
          <div className="eyebrow text-white/80 mb-2 inline-flex items-center gap-2">
            <Heart className="w-3 h-3" /> A charity we support
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.0] mb-3">
            {charity.name}
          </h2>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
            {charity.tagline}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href={charity.donate_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white text-[#0e0e0e] hover:bg-[#fef3c7] transition-colors text-xs mono uppercase tracking-[0.18em] font-bold shadow-[0_2px_0_0_#0e0e0e]"
              data-testid={`charity-donate-${charity.slug}`}
            >
              <Heart className="w-3.5 h-3.5 text-[#dc2626] fill-current" />
              Donate via JustGiving
              <ExternalLink className="w-3 h-3" />
            </a>
            {total > 0 && (
              <span className="inline-flex items-center gap-1 bg-[#fef3c7] text-[#0e0e0e] px-3 py-1.5 mono text-[12px] uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> {formatGBP(total)} raised so far
              </span>
            )}
          </div>
        </div>
      </div>

      {/* What they do */}
      <div className="p-6 sm:p-8 border-b hairline">
        <div className="eyebrow mb-2">What they do</div>
        <p className="text-base text-[#222] leading-relaxed">
          {charity.description}
        </p>
      </div>

      {/* Stats grid */}
      {charity.stats?.length > 0 && (
        <div className="p-6 sm:p-8 border-b hairline">
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#ff4500]" /> The work in numbers
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {charity.stats.map((s, i) => {
              const Icon = STAT_ICONS[s.label] || Sparkles;
              return (
                <div key={i} className="border hairline p-4 bg-[#fafafa]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1.5 bg-[#0e0e0e] text-white">
                      <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
                    </div>
                    <div className="text-[10px] mono uppercase tracking-wider text-[#666]">{s.label}</div>
                  </div>
                  <div className="font-display text-2xl tracking-tight mb-1">{s.value}</div>
                  <div className="text-[11px] text-[#555] leading-snug">{s.detail}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Highlights */}
      {charity.highlights?.length > 0 && (
        <div className="p-6 sm:p-8 border-b hairline bg-[#fffaf6]">
          <div className="eyebrow mb-3 inline-flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 text-[#ff4500]" /> What stops you in your tracks
          </div>
          <ul className="space-y-3">
            {charity.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-[#222] leading-relaxed">
                <span className="text-[#ff4500] mt-1.5 text-lg leading-none flex-shrink-0">▸</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Why we support */}
      {charity.why_we_support && (
        <div className="p-6 sm:p-8 bg-white">
          <div className="eyebrow mb-2 inline-flex items-center gap-2 text-[#ff4500]">
            <Heart className="w-3 h-3 fill-current" /> Why we support them
          </div>
          <p className="text-base text-[#222] leading-relaxed">
            {charity.why_we_support}
          </p>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="p-6 sm:p-8 bg-[#0e0e0e] text-white text-center">
        <a
          href={charity.donate_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff4500] text-white hover:bg-[#e63e00] transition-colors text-sm mono uppercase tracking-[0.18em] font-bold shadow-[0_3px_0_0_#fff]"
        >
          <Heart className="w-4 h-4 fill-current" />
          Donate to {charity.name}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <div className="text-[11px] mono text-white/60 uppercase tracking-wider mt-3">
          100% goes to the charity · CampCompass takes nothing
        </div>
      </div>
    </section>
  );
}

export default function Charities() {
  const [charities, setCharities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/charities"),
      api.get("/charity/donations"),
    ]).then(([cRes, dRes]) => {
      if (!alive) return;
      setCharities(cRes.data?.charities || []);
      setTotal(Number(dRes.data?.total_raised_gbp || 0));
    }).catch(() => { /* swallow */ })
    .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="charities-page">
      <Header active="charities" />

      {/* HERO */}
      <div className="bg-[#0e0e0e] text-white border-b-2 border-[#ff4500]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider opacity-70 hover:opacity-100 mb-5">
            <ChevronLeft className="w-3 h-3" /> Back to camp
          </Link>
          <div className="eyebrow text-[#ff4500] mb-3 inline-flex items-center gap-2">
            <Mountain className="w-3 h-3" strokeWidth={2.4} /> Charities · the work behind the hills
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-4">
            Camping means <br />
            <span className="text-[#ff4500]">someone has your back.</span>
          </h1>
          <p className="text-lg text-white/85 leading-relaxed max-w-2xl">
            Every camper, walker and wild-swimmer in the UK is quietly looked after by volunteer
            charities you may never have heard of. CampCompass supports them. Here's who we back,
            why, and how you can chip in.
          </p>
          {total > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#16a34a] text-white px-4 py-2 mono text-xs uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              {formatGBP(total)} raised so far · thank you
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#555]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading charities…
          </div>
        ) : charities.length === 0 ? (
          <div className="text-center py-20 text-[#555]">
            No charities listed yet — check back soon.
          </div>
        ) : (
          charities.map((c) => <CharityPanel key={c.slug} charity={c} total={total} />)
        )}
      </div>
    </div>
  );
}
